import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generates settlement document PDF with group financial data
 * Includes: 매출액, 미수금, 전도금, 예상수익
 */

// A4 dimensions in mm
const A4_WIDTH = 210;
const A4_HEIGHT = 297;
const MARGIN = 15;
const PAGE_MARGIN_TOP = 15;
const PAGE_MARGIN_BOTTOM = 15;
const CONTENT_WIDTH = A4_WIDTH - (MARGIN * 2);

// DOM rendering target widths (px) aligned to A4 at 96dpi (~3.7795 px/mm)
const DOM_PAGE_PX_WIDTH = 794; // ~210mm * 3.7795
const DOM_CONTENT_PX_WIDTH = Math.round(DOM_PAGE_PX_WIDTH * (CONTENT_WIDTH / A4_WIDTH));

// Colors - Formal business document design
const COLORS = {
  primary: '#1a1a1a',
  secondary: '#4a4a4a',
  text: '#1a1a1a',
  textLight: '#666666',
  lightGray: '#f5f5f5',
  border: '#d0d0d0',
  borderDark: '#a0a0a0',
  white: '#ffffff',
  tableHeader: '#e8e8e8',
};

/**
 * Format number as Korean currency
 */
const fmtMoney = (v) => {
  if (v === null || v === undefined || v === '') return '0';
  const num = Math.trunc(Number(String(v).replace(/,/g, '')));
  if (!Number.isFinite(num)) return String(v);
  return num.toLocaleString('ko-KR');
};

/**
 * Create HTML element for settlement document rendering
 */
function createSettlementHTMLElement(settlementData) {
  const {
    groupName,
    groupNo,
    saleNo,
    startDate,
    endDate,
    managerName,
    revenue, // 행사매출액
    receivable, // 행사미수금
    preDeposit, // 행사전도금
    expectedProfit, // 행사예상수익
    revenueBreakdown, // 매출액 상세 (array of orders)
    receivableBreakdown, // 미수금 상세 (array of orders)
    preDepositBreakdown, // 전도금 상세 (array of pre-deposits)
    depositBreakdown, // 입금 리스트 상세 (array of deposits)
    remark, // 비고
  } = settlementData;

  const container = document.createElement('div');
  container.style.cssText = `
    width: ${DOM_CONTENT_PX_WIDTH}px;
    background: white;
    font-family: 'Pretendard', 'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    padding: 0px;
    box-sizing: border-box;
    color: ${COLORS.text};
  `;

  // Header - Settlement Document Title
  const header = document.createElement('div');
  header.style.cssText = `
    text-align: center;
    padding: 0 0 20px 0;
    margin: 0 0 28px 0;
    border-bottom: 3px double ${COLORS.borderDark};
  `;
  header.innerHTML = `
    <h1 style="margin: 0 0 8px 0; font-size: 28px; font-weight: 700; color: ${COLORS.primary}; letter-spacing: -0.5px;">정 산 서</h1>
    <div style="font-size: 13px; color: ${COLORS.secondary}; letter-spacing: 1px;">SETTLEMENT DOCUMENT</div>
  `;
  container.appendChild(header);

  // Document Info Section
  const docInfoSection = document.createElement('div');
  docInfoSection.style.cssText = `
    background: ${COLORS.lightGray};
    border: 1px solid ${COLORS.border};
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 32px;
  `;
  
  const docInfoGrid = document.createElement('div');
  docInfoGrid.style.cssText = `
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    font-size: 13px;
  `;

  const docInfoItems = [
    { label: '단체명', value: groupName || '-' },
    { label: '단체번호', value: groupNo || '-' },
    { label: '판매번호', value: saleNo || '-' },
    { label: '담당자', value: managerName || '-' },
    { label: '여행기간', value: startDate && endDate ? `${startDate} ~ ${endDate}` : '-' },
    { label: '발행일', value: new Date().toLocaleDateString('ko-KR') },
  ];

  docInfoItems.forEach(item => {
    const itemDiv = document.createElement('div');
    itemDiv.style.cssText = 'display: flex; align-items: center;';
    itemDiv.innerHTML = `
      <span style="font-weight: 600; color: ${COLORS.textLight}; min-width: 80px;">${item.label}:</span>
      <span style="color: ${COLORS.text};">${item.value}</span>
    `;
    docInfoGrid.appendChild(itemDiv);
  });

  docInfoSection.appendChild(docInfoGrid);
  container.appendChild(docInfoSection);

  // Financial Summary Section - Main highlight
  const summarySection = document.createElement('div');
  summarySection.style.cssText = `
    margin-bottom: 32px;
  `;

  const summaryTitle = document.createElement('div');
  summaryTitle.style.cssText = `
    font-size: 18px;
    font-weight: 700;
    color: ${COLORS.text};
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid ${COLORS.borderDark};
  `;
  summaryTitle.textContent = '정산 요약';
  summarySection.appendChild(summaryTitle);

  const summaryGrid = document.createElement('div');
  summaryGrid.style.cssText = `
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  `;

  const summaryItems = [
    { label: '행사 매출액', value: revenue },
    { label: '행사 미수금', value: receivable },
    { label: '행사 전도금', value: preDeposit },
    { label: '행사 예상수익', value: expectedProfit },
  ];

  summaryItems.forEach(item => {
    const card = document.createElement('div');
    card.style.cssText = `
      background: ${COLORS.white};
      border: 1px solid ${COLORS.border};
      border-radius: 2px;
      padding: 14px 16px;
    `;
    card.innerHTML = `
      <div style="font-size: 12px; color: ${COLORS.textLight}; font-weight: 600; margin-bottom: 8px;">${item.label}</div>
      <div style="font-size: 20px; font-weight: 700; color: ${COLORS.text}; text-align: right;">
        ${fmtMoney(item.value)} 원
      </div>
    `;
    summaryGrid.appendChild(card);
  });

  summarySection.appendChild(summaryGrid);
  container.appendChild(summarySection);

  // Revenue Breakdown Section (always show section; show table only when data exists)
  {
    const rows = Array.isArray(revenueBreakdown) ? revenueBreakdown : [];
    if (rows.length > 0) {
      const revenueSection = createBreakdownSection(
        '매출액 상세 내역',
        rows,
        ['주문번호', '주문자', '상품금액', '택스및유류', '추가 판매가', '프로모션할인', '고객결제가']
      );
      container.appendChild(revenueSection);
    } else {
      const emptySection = document.createElement('div');
      emptySection.style.cssText = `
        margin-bottom: 32px;
        page-break-inside: avoid;
      `;
      emptySection.innerHTML = `
        <div style="font-size: 15px; font-weight: 700; color: ${COLORS.text}; margin-bottom: 12px; padding: 8px 12px; background: ${COLORS.tableHeader}; border-left: 4px solid ${COLORS.primary};">매출액 상세 내역</div>
        <div style="font-size: 12px; color: ${COLORS.textLight}; padding: 12px 8px; border: 1px solid ${COLORS.border}; border-radius: 4px; text-align: center;">내역 없음</div>
      `;
      container.appendChild(emptySection);
    }
  }

  // Deposit List (입금 리스트) Section (always show section; show table only when data exists)
  {
    const rows = Array.isArray(depositBreakdown) ? depositBreakdown : [];
    if (rows.length > 0) {
      const depositSection = createBreakdownSection(
        '입금 리스트',
        rows,
        ['수납일', '송금카드소유자', '결제구분', '계좌카드번호', '입금액']
      );
      container.appendChild(depositSection);
    } else {
      const emptySection = document.createElement('div');
      emptySection.style.cssText = `
        margin-bottom: 32px;
        page-break-inside: avoid;
      `;
      emptySection.innerHTML = `
        <div style="font-size: 15px; font-weight: 700; color: ${COLORS.text}; margin-bottom: 12px; padding: 8px 12px; background: ${COLORS.tableHeader}; border-left: 4px solid ${COLORS.primary};">입금 리스트</div>
        <div style="font-size: 12px; color: ${COLORS.textLight}; padding: 12px 8px; border: 1px solid ${COLORS.border}; border-radius: 4px; text-align: center;">내역 없음</div>
      `;
      container.appendChild(emptySection);
    }
  }

  // Receivable Breakdown Section (always show section; show table only when data exists)
  {
    const rows = Array.isArray(receivableBreakdown) ? receivableBreakdown : [];
    if (rows.length > 0) {
      const receivableSection = createBreakdownSection(
        '미수금 상세 내역',
        rows,
        ['주문번호', '주문자', '고객결제가', '입금액', '미수액']
      );
      container.appendChild(receivableSection);
    } else {
      const emptySection = document.createElement('div');
      emptySection.style.cssText = `
        margin-bottom: 32px;
        page-break-inside: avoid;
      `;
      emptySection.innerHTML = `
        <div style="font-size: 15px; font-weight: 700; color: ${COLORS.text}; margin-bottom: 12px; padding: 8px 12px; background: ${COLORS.tableHeader}; border-left: 4px solid ${COLORS.primary};">미수금 상세 내역</div>
        <div style="font-size: 12px; color: ${COLORS.textLight}; padding: 12px 8px; border: 1px solid ${COLORS.border}; border-radius: 4px; text-align: center;">내역 없음</div>
      `;
      container.appendChild(emptySection);
    }
  }

  // Pre-Deposit (전도금 출금) Breakdown Section (always show section; show table only when data exists)
  {
    const rows = Array.isArray(preDepositBreakdown) ? preDepositBreakdown : [];
    if (rows.length > 0) {
      const preDepSection = createPreDepositSection(rows);
      container.appendChild(preDepSection);
    } else {
      const emptySection = document.createElement('div');
      emptySection.style.cssText = `
        margin-bottom: 32px;
        page-break-inside: avoid;
      `;
      emptySection.innerHTML = `
        <div style="font-size: 15px; font-weight: 700; color: ${COLORS.text}; margin-bottom: 12px; padding: 8px 12px; background: ${COLORS.tableHeader}; border-left: 4px solid ${COLORS.primary};">전도금 출금 내역</div>
        <div style="font-size: 12px; color: ${COLORS.textLight}; padding: 12px 8px; border: 1px solid ${COLORS.border}; border-radius: 4px; text-align: center;">내역 없음</div>
      `;
      container.appendChild(emptySection);
    }
  }

  // Remark Section
  if (remark && String(remark).trim()) {
    const remarkSection = document.createElement('div');
    remarkSection.style.cssText = `
      margin-top: 32px;
      padding: 20px;
      background: ${COLORS.lightGray};
      border: 1px solid ${COLORS.border};
      border-radius: 8px;
    `;
    remarkSection.innerHTML = `
      <div style="font-size: 14px; font-weight: 600; color: ${COLORS.text}; margin-bottom: 8px;">비고</div>
      <div style="font-size: 13px; color: ${COLORS.textLight}; line-height: 1.6; white-space: pre-wrap;">${String(remark)}</div>
    `;
    container.appendChild(remarkSection);
  }

  // Footer
  const footer = document.createElement('div');
  footer.style.cssText = `
    margin-top: 48px;
    padding-top: 20px;
    border-top: 1px solid ${COLORS.border};
    text-align: center;
    color: ${COLORS.textLight};
    font-size: 11px;
  `;
  footer.innerHTML = `
    <div style="margin-bottom: 4px;">본 정산서는 ${new Date().toLocaleDateString('ko-KR')} 기준으로 작성되었습니다.</div>
    <div style="font-size: 10px; color: ${COLORS.secondary};">Travel Agency ERP System</div>
  `;
  container.appendChild(footer);

  return container;
}

/**
 * Create breakdown table section
 */
function createBreakdownSection(title, data, columns) {
  const section = document.createElement('div');
  section.style.cssText = `
    margin-bottom: 32px;
    page-break-inside: avoid;
  `;

  const sectionTitle = document.createElement('div');
  sectionTitle.style.cssText = `
    font-size: 15px;
    font-weight: 700;
    color: ${COLORS.text};
    margin-bottom: 12px;
    padding: 8px 12px;
    background: ${COLORS.tableHeader};
    border-left: 4px solid ${COLORS.primary};
  `;
  sectionTitle.textContent = title;
  section.appendChild(sectionTitle);

  const table = document.createElement('table');
  table.style.cssText = `
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
    margin-top: 8px;
  `;

  // Table header
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  headerRow.style.cssText = `background: ${COLORS.tableHeader}; color: ${COLORS.text};`;
  
  columns.forEach(col => {
    const th = document.createElement('th');
    th.style.cssText = `
      padding: 10px 8px;
      text-align: center;
      font-weight: 600;
      border: 1px solid ${COLORS.borderDark};
    `;
    th.textContent = col;
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);
  table.appendChild(thead);

  // Table body
  const tbody = document.createElement('tbody');
  data.forEach((row, idx) => {
    const tr = document.createElement('tr');
    tr.style.cssText = `
      background: ${idx % 2 === 0 ? COLORS.white : COLORS.lightGray};
    `;

    columns.forEach(col => {
      const td = document.createElement('td');
      td.style.cssText = `
        padding: 8px;
        text-align: center;
        border: 1px solid ${COLORS.border};
      `;
      
      const value = row[col];
      // Format money columns (including 추가서비스 and 할인)
      if (col.includes('금액') || col.includes('유류') || col.includes('결제가') || col.includes('입금') || col.includes('미수') || col.includes('서비스') || col.includes('할인')) {
        if (col.includes('할인')) {
          const num = Number(String(value ?? '').replace(/,/g, '').replace(/[^0-9-]/g, ''));
          const isNum = Number.isFinite(num);
          const absText = fmtMoney(Math.abs(isNum ? num : 0));
          td.style.color = '#dc2626';
          td.textContent = `-${absText}`; // always show visually negative
        } else {
          td.textContent = fmtMoney(value);
        }
      } else {
        td.textContent = value || '-';
      }
      
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);

  // Table footer with totals
  const tfoot = document.createElement('tfoot');
  const footerRow = document.createElement('tr');
  footerRow.style.cssText = `
    background: ${COLORS.tableHeader};
    font-weight: 700;
    border-top: 2px solid ${COLORS.borderDark};
  `;

  columns.forEach((col, idx) => {
    const td = document.createElement('td');
    td.style.cssText = `
      padding: 10px 8px;
      text-align: center;
      border: 1px solid ${COLORS.borderDark};
    `;

    if (idx === 0) {
      td.textContent = '합계';
      td.colSpan = 2;
    } else if (idx === 1) {
      // Skip because of colspan
      return;
    } else {
      // Calculate sum for numeric columns
      if (col.includes('금액') || col.includes('유류') || col.includes('결제가') || col.includes('입금') || col.includes('미수') || col.includes('서비스') || col.includes('할인')) {
        let sum = data.reduce((acc, row) => {
          const raw = String(row[col] || '');
          const cleaned = raw.replace(/,/g, '').replace(/[^0-9-]/g, '');
          const num = Number(cleaned);
          if (!Number.isFinite(num)) return acc;
          if (col.includes('할인')) {
            // Discounts should reduce totals; treat positive as negative
            return acc + (num > 0 ? -num : num);
          }
          return acc + num;
        }, 0);
        if (col.includes('할인')) {
          td.style.color = '#dc2626';
        }
        td.textContent = fmtMoney(sum);
      } else {
        td.textContent = '-';
      }
    }
    footerRow.appendChild(td);
  });
  tfoot.appendChild(footerRow);
  table.appendChild(tfoot);

  section.appendChild(table);
  return section;
}

/**
 * Create pre-deposit breakdown section
 */
function createPreDepositSection(data) {
  const section = document.createElement('div');
  section.style.cssText = `
    margin-bottom: 32px;
    page-break-inside: avoid;
  `;

  const sectionTitle = document.createElement('div');
  sectionTitle.style.cssText = `
    font-size: 15px;
    font-weight: 700;
    color: ${COLORS.text};
    margin-bottom: 12px;
    padding: 8px 12px;
    background: ${COLORS.tableHeader};
    border-left: 4px solid ${COLORS.primary};
  `;
  sectionTitle.textContent = '전도금 상세 내역';
  section.appendChild(sectionTitle);

  const table = document.createElement('table');
  table.style.cssText = `
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    margin-top: 8px;
  `;

  // Table header
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');
  headerRow.style.cssText = `background: ${COLORS.tableHeader}; color: ${COLORS.text};`;
  
  const columns = ['출납일', '지급액', '종류', '결제구분', '송금카드소유자', '비고'];
  columns.forEach(col => {
    const th = document.createElement('th');
    th.style.cssText = `
      padding: 10px 8px;
      text-align: center;
      font-weight: 600;
      border: 1px solid ${COLORS.borderDark};
    `;
    th.textContent = col;
    headerRow.appendChild(th);
  });
  thead.appendChild(headerRow);
  table.appendChild(thead);

  // Table body
  const tbody = document.createElement('tbody');
  data.forEach((row, idx) => {
    const tr = document.createElement('tr');
    tr.style.cssText = `
      background: ${idx % 2 === 0 ? COLORS.white : COLORS.lightGray};
    `;

    // 출납일
    let td = document.createElement('td');
    td.style.cssText = `padding: 8px; text-align: center; border: 1px solid ${COLORS.border};`;
    td.textContent = row.매핑출납일 || row.지급일 || row.paymentDate || '-';
    tr.appendChild(td);

    // 지급액
    td = document.createElement('td');
    td.style.cssText = `padding: 8px; text-align: right; border: 1px solid ${COLORS.border};`;
    td.textContent = fmtMoney(row.지급액 || row.amount);
    tr.appendChild(td);

    // 종류
    td = document.createElement('td');
    td.style.cssText = `padding: 8px; text-align: center; border: 1px solid ${COLORS.border}; white-space: nowrap;`;
    td.textContent = row.매핑종류 || '-';
    tr.appendChild(td);

    // 결제구분
    td = document.createElement('td');
    td.style.cssText = `padding: 8px; text-align: center; border: 1px solid ${COLORS.border}; white-space: nowrap;`;
    td.textContent = row.매핑결제구분 || '-';
    tr.appendChild(td);

    // 송금카드소유자
    td = document.createElement('td');
    td.style.cssText = `padding: 8px; text-align: center; border: 1px solid ${COLORS.border}; white-space: nowrap;`;
    td.textContent = row.매핑송금카드소유자 || '-';
    tr.appendChild(td);

    // 비고
    td = document.createElement('td');
    td.style.cssText = `padding: 8px; text-align: center; border: 1px solid ${COLORS.border};`;
    td.textContent = row.비고 || row.remark || '-';
    tr.appendChild(td);

    tbody.appendChild(tr);
  });
  table.appendChild(tbody);

  // Table footer with total
  const tfoot = document.createElement('tfoot');
  const footerRow = document.createElement('tr');
  footerRow.style.cssText = `
    background: ${COLORS.tableHeader};
    font-weight: 700;
    border-top: 2px solid ${COLORS.borderDark};
  `;

  let td = document.createElement('td');
  td.style.cssText = `padding: 10px 8px; text-align: center; border: 1px solid ${COLORS.borderDark};`;
  td.textContent = '합계';
  footerRow.appendChild(td);

  // 지급액 합계
  td = document.createElement('td');
  td.style.cssText = `padding: 10px 8px; text-align: right; border: 1px solid ${COLORS.borderDark};`;
  const totalAmount = data.reduce((acc, row) => {
    const raw = String(row.지급액 || row.amount || '');
    const cleaned = raw.replace(/,/g, '').replace(/[^0-9-]/g, '');
    const num = Number(cleaned);
    const val = Number.isFinite(num) ? num : 0;
    return acc + val;
  }, 0);
  td.textContent = fmtMoney(totalAmount);
  footerRow.appendChild(td);

  // 나머지 4개 칸은 '-' 표시
  for (let i = 0; i < 4; i++) {
    td = document.createElement('td');
    td.style.cssText = `padding: 10px 8px; text-align: center; border: 1px solid ${COLORS.borderDark};`;
    td.textContent = '-';
    footerRow.appendChild(td);
  }

  tfoot.appendChild(footerRow);
  table.appendChild(tfoot);

  section.appendChild(table);
  return section;
}

/**
 * Render HTML element to canvas
 */
async function renderToCanvas(element) {
  return await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    windowWidth: 794,
    imageTimeout: 8000,
    removeContainer: true,
  });
}

/**
 * Add canvas to PDF by slicing into multiple pages
 */
async function addCanvasAsPages(pdf, canvas) {
  const targetWidthMM = A4_WIDTH - (MARGIN * 2);
  const pageHeightMM = A4_HEIGHT - PAGE_MARGIN_TOP - PAGE_MARGIN_BOTTOM;
  const pxPerMm = canvas.width / targetWidthMM;
  const pageHeightPx = Math.floor(pageHeightMM * pxPerMm);

  let y = 0;
  let pageIndex = 0;
  
  while (y < canvas.height) {
    const sliceHeightPx = Math.min(pageHeightPx, canvas.height - y);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;
    const ctx = sliceCanvas.getContext('2d');
    ctx.drawImage(
      canvas,
      0, y, canvas.width, sliceHeightPx,
      0, 0, canvas.width, sliceHeightPx
    );

    const imgData = sliceCanvas.toDataURL('image/png');
    const imgWidthMM = targetWidthMM;
    const imgHeightMM = (sliceHeightPx / canvas.width) * imgWidthMM;

    if (pageIndex > 0) {
      pdf.addPage();
    }
    pdf.addImage(
      imgData,
      'PNG',
      MARGIN,
      PAGE_MARGIN_TOP,
      imgWidthMM,
      imgHeightMM,
      undefined,
      'FAST'
    );

    y += sliceHeightPx;
    pageIndex += 1;

    // Yield to UI
    await new Promise((r) => setTimeout(r, 0));
  }
}

export async function openSettlementPrintPreview(settlementData) {
  try {
    const groupName = (settlementData?.groupName || '단체').toString().replace(/["<>:|?*]/g, ' ');
    const today = new Date().toISOString().split('T')[0];

    const el = createSettlementHTMLElement(settlementData);

    const win = window.open('', '_blank');
    if (!win) throw new Error('팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.');

    const html = `<!DOCTYPE html>
<html lang="ko">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>정산서 - ${groupName}</title>
    <style>
      html, body { height: 100%; }
      body { margin: 0; background: #f3f4f6; font-family: 'Pretendard','Malgun Gothic','맑은 고딕','Apple SD Gothic Neo',system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; color: #111827; }
      .toolbar { position: sticky; top: 0; z-index: 100; background: #ffffff; border-bottom: 1px solid #e5e7eb; padding: 10px 12px; display: flex; gap: 8px; align-items: center; }
      .toolbar-title { font-weight: 600; font-size: 14px; margin-right: auto; color: #111827; }
      .btn { appearance: none; border: 1px solid #d1d5db; background: #ffffff; color: #111827; padding: 6px 10px; border-radius: 6px; font-size: 12px; cursor: pointer; }
      .btn.primary { background: #2563eb; color: #ffffff; border-color: #2563eb; }
      .btn:hover { filter: brightness(0.98); }
      .sheet { width: 210mm; min-height: 297mm; margin: 12px auto; background: #ffffff; box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 1px 6px rgba(0,0,0,0.08); }
      .content { padding: 12mm; }
      @media print {
        @page { size: A4; margin: 10mm; }
        html, body { height: auto; }
        body { background: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .toolbar { display: none !important; }
        .sheet { box-shadow: none !important; margin: 0 !important; width: 210mm !important; min-height: 297mm !important; }
        .content { padding: 0 !important; }
      }
    </style>
  </head>
  <body>
    <div class="toolbar">
      <div class="toolbar-title">정산서 · ${groupName} (${today})</div>
      <button class="btn" onclick="window.close()">닫기</button>
      <button class="btn primary" onclick="window.print()">인쇄</button>
    </div>
    <div class="sheet">
      <div class="content">${el.outerHTML}</div>
    </div>
  </body>
</html>`;

    win.document.open();
    win.document.write(html);
    win.document.close();
    try { win.focus(); } catch (_) {}
  } catch (error) {
    console.error('Settlement print preview error:', error);
    throw error;
  }
}

/**
 * Export settlement document to PDF
 * @param {Object} settlementData - Settlement data including financial information
 */
export async function exportSettlementPDF(settlementData) {
  try {
    const groupName = settlementData.groupName || '단체';

    // Create PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
    });

    // Create HTML element
    const element = createSettlementHTMLElement(settlementData);

    // Temporarily add to DOM (hidden)
    element.style.position = 'fixed';
    element.style.left = '-9999px';
    element.style.top = '0';
    document.body.appendChild(element);

    try {
      // Wait for layout
      await new Promise(resolve => setTimeout(resolve, 50));

      // Convert to canvas
      const canvas = await renderToCanvas(element);

      // Add to PDF
      await addCanvasAsPages(pdf, canvas);

      // Save PDF
      const fileName = `정산서_${groupName}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      return fileName;
    } finally {
      // Remove from DOM
      try {
        document.body.removeChild(element);
      } catch (_) {}
    }
  } catch (error) {
    console.error('Settlement PDF generation error:', error);
    throw error;
  }
}
